import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';

import { BaseViewModel, BaseViewModelState } from '../../../shared/view-model/base-view-model';
import { MeasuredDataDetailsDataService } from './measured-data-details-data.service';
import { RecordData, TypesUtils } from '../../../types';
import { DatabaseCacheService } from '../../../data/cache/database-cache.service';
import { MsalService } from '@azure/msal-angular';
import { GostUtils } from '../cards/recordings/gost-utils';

export interface MeasuredDataDetailsState extends BaseViewModelState {
  recordData: RecordData | null;
  mediaRecordData: any | null;
  recordLoading: boolean;
  mediaLoading: boolean;
  recordError: string | null;
  mediaError: string | null;
  recordType: number;
  showRsstChart: boolean;
  showMwstView: boolean;
  showMonitoringView: boolean;
  beforeMealImage: string | null;
  afterMealImage: string | null;
  recordLoadingSuccess: boolean;
}

const initialState: MeasuredDataDetailsState = {
  status: 'idle',
  errorMessage: null,
  recordData: null,
  mediaRecordData: null,
  recordLoading: false,
  mediaLoading: false,
  recordError: null,
  mediaError: null,
  recordType: -1,
  showRsstChart: false,
  showMwstView: false,
  showMonitoringView: false,
  beforeMealImage: null,
  afterMealImage: null,
  recordLoadingSuccess: true,
};

@Injectable()
export class MeasuredDataDetailsViewModel extends BaseViewModel<MeasuredDataDetailsState> {
  private readonly dataService = inject(MeasuredDataDetailsDataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cache = inject(DatabaseCacheService);
  private readonly msalService = inject(MsalService);
  private readonly gostUtils = inject(GostUtils);

  constructor() {
    super(initialState);

    this.dataService.record$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (record) => {
          if (!record) {
            if (this.currentState.recordLoading) {
              const message = 'RECORD_CONVERSION_FAILED';
              this.patchState({
                status: 'error',
                errorMessage: message,
                recordLoading: false,
                recordError: message,
                recordLoadingSuccess: false,
              });
            }
            return;
          }

          try {
            const metadata = this.buildRecordMetadata(record);
            this.patchState({
              status: 'success',
              recordData: record,
              recordLoading: false,
              recordError: null,
              errorMessage: null,
              ...metadata,
            });
          } catch (error: any) {
            const message = error?.message ?? 'RECORD_METADATA_BUILD_FAILED';
            this.patchState({
              status: 'error',
              errorMessage: message,
              recordLoading: false,
              recordError: message,
              recordLoadingSuccess: false,
            });
            console.error('[ERROR] MeasuredDataDetailsViewModel > record$ next()', error);
          }
        },
        error: (error: any) => {
          const message = error?.message ?? 'RECORD_STREAM_FAILED';
          this.patchState({
            status: 'error',
            errorMessage: message,
            recordLoading: false,
            recordError: message,
            recordLoadingSuccess: false,
          });
          console.error('[ERROR] MeasuredDataDetailsViewModel > record$ subscription', error);
        },
      });

    this.dataService.media$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(media => {
        this.patchState({
          mediaRecordData: media,
          mediaLoading: false,
          mediaError: null,
        });
      });
  }

  async loadRecord(recordId: string): Promise<void> {
    // 新しいレコードの読込開始時に一度ステートを初期化し、旧データが一瞬表示されるのを防ぐ
    this.patchState({
      status: 'loading',
      errorMessage: null,
      recordLoading: true,
      recordError: null,
      recordData: null,
      mediaRecordData: null,
      mediaLoading: false,
      mediaError: null,
      recordType: -1,
      showRsstChart: false,
      showMwstView: false,
      showMonitoringView: false,
      beforeMealImage: null,
      afterMealImage: null,
      recordLoadingSuccess: true,
    });

    if (recordId === this.gostUtils.newGostId) {
      const record = this.createNewGostRecord();
      const metadata = this.buildRecordMetadata(record);
      this.patchState({
        status: 'success',
        recordData: record,
        recordLoading: false,
        recordError: null,
        errorMessage: null,
        recordLoadingSuccess: true,
        ...metadata,
      });
      return;
    }

    try {
      await firstValueFrom(this.dataService.loadRecord(recordId));
    } catch (error: any) {
      const message = error?.message ?? 'RECORD_LOAD_FAILED';
      this.patchState({
        status: 'error',
        errorMessage: message,
        recordLoading: false,
        recordError: message,
        recordLoadingSuccess: false,
      });
      throw error;
    }
  }

  async loadMedia(recordId: string): Promise<void> {
    this.patchState({
      mediaLoading: true,
      mediaError: null,
    });
    try {
      await firstValueFrom(this.dataService.loadMedia(recordId));
    } catch (error: any) {
      const message = error?.message ?? 'MEDIA_LOAD_FAILED';
      this.patchState({
        mediaLoading: false,
        mediaError: message,
      });
      console.error('[ERROR] MeasuredDataDetailsViewModel > loadMedia()', error);
    }
  }

  // レコードの種類に応じた付帯情報をまとめて算出
  private buildRecordMetadata(record: RecordData) {
    const recordData: any = record?.data ?? {};
    const storage: any = recordData.storage ?? {};
    const recordType = recordData.recordType;
    let resolvedType = -1;
    let showRsstChart = false;
    let showMwstView = false;
    let showMonitoringView = false;

    switch (recordType) {
      case 'RSST':
        resolvedType = 0;
        showRsstChart = true;
        break;
      case 'MWST':
      case 'FOOD_TEST':
        resolvedType = 0;
        showMwstView = true;
        break;
      case 'MONITORING':
      case 'MEAL_MONITORING':
      case 'MEAL_MONITORING_WITH_VITALS':
        resolvedType = 0;
        showMonitoringView = true;
        break;
      case 'VITAL':
        resolvedType = 1;
        break;
      case 'GOST':
      case 'GOST_DATA':
        resolvedType = recordData.version === '2' ? 7 : 2;
        break;
      case 'MWST_G':
      case 'MWST_G_DATA':
      case 'FOOD_TEST_G':
      case 'FOOD_TEST_G_DATA':
        resolvedType = 3;
        showMwstView = true;
        break;
      case 'VS':
        resolvedType = 4;
        break;
      case 'TOMASS':
        resolvedType = 5;
        break;
      case 'SSTP':
      case 'SSTP_DATA':
        resolvedType = 6;
        break;
      default:
        resolvedType = -1;
        break;
    }

    return {
      recordType: resolvedType,
      showRsstChart,
      showMwstView,
      showMonitoringView,
      beforeMealImage: storage.beforeMealFoodImage ?? null,
      afterMealImage: storage.afterMealFoodImage ?? null,
      recordLoadingSuccess: true,
    };
  }

  private createNewGostRecord(): RecordData {
    const emptyGost = this.gostUtils.createEmptyGost2(this.cache, this.msalService);
    const result = TypesUtils.convertJsonToRecords([emptyGost], false);
    const record = result?.records?.[0] as RecordData | undefined;
    if (!record) {
      throw new Error('RECORD_CONVERSION_FAILED');
    }
    return record;
  }

  setMediaRecordData(media: any): void {
    this.patchState({
      mediaRecordData: media,
      mediaLoading: false,
      mediaError: null,
    });
  }
}
